import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardList, ArrowDownToLine } from 'lucide-react';
import DashboardLayout, { NavCard } from '../../components/DashboardLayout';
import { usePerms } from '../../permissions/PermissionsContext';

/**
 * MerchantWithdrawalHub — iter-264c (Feb 2026)
 * Groups every withdrawal-related screen for the merchant platform:
 *   • إدراج طلب سحب (manual entry, useful for unregistered agents)
 *   • طلبات السحب  (review / approve list)
 */
export default function MerchantWithdrawalHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const items = [
    {
      id: 'insert-withdrawal-request',
      titleAr: 'إدراج طلب سحب',
      titleEn: 'Insert Withdrawal Request',
      descAr: 'إنشاء طلب سحب يدوي لوكيل مسجل أو غير مسجل',
      descEn: 'Manually create a withdrawal request for a registered or unregistered agent',
      icon: ClipboardList,
      route: '/dashboard/finance/cloudgm/merchants/insert-withdrawal-request',
      color: '#f97316',
      screenCode: 'FIN-MERCH-WD-INS',
      testId: 'card-insert-withdrawal-request',
    },
    {
      id: 'withdrawal-requests',
      titleAr: 'طلبات السحب',
      titleEn: 'Withdrawal Requests',
      descAr: 'مراجعة واعتماد طلبات سحب التجار من المحفظة',
      descEn: 'Review and approve merchant wallet withdrawal requests',
      icon: ArrowDownToLine,
      route: '/dashboard/finance/cloudgm/merchants/withdrawal-requests',
      color: '#10b981',
      screenCode: 'FIN-MERCH-WD-01',
      testId: 'card-withdrawal-requests',
    },
  ];

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  return (
    <DashboardLayout
      title="السحب"
      titleEn="Withdrawal"
      subtitle="إدارة سحوبات الوكلاء والتجار"
      subtitleEn="Manage agent and merchant withdrawals"
      screenCode="FIN-CGM-MERCH-WD"
      backPath="/dashboard/finance/cloudgm/merchants"
      breadcrumb={[
        { labelAr: 'المالية',                labelEn: 'Finance',                     path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC',         labelEn: 'CLOUDGM FZ LLC',              path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)',  labelEn: 'Merchant Platform (Finance)', path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'السحب',                  labelEn: 'Withdrawal',                  path: null },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {visiblePages(items, 'route').map((item) => (
            <NavCard
              key={item.id}
              icon={item.icon}
              title={item.titleAr}
              titleEn={item.titleEn}
              description={item.descAr}
              descriptionEn={item.descEn}
              onClick={() => navigate(item.route)}
              color={item.color}
              screenCode={item.screenCode}
              testId={item.testId}
            />
          ))}
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
